import GUI from 'lil-gui'
import Stats from 'stats.js'
import * as THREE from 'three'
import { TransformControls } from 'three/addons/controls/TransformControls.js'

// Debug 基礎設施 + 所有 Studio 元件 debug 的集中地（#debug 時才啟用）：
//   - 可拖動面板（lil-gui + stats.js）
//   - 各元件的 GUI 面板：元件建立時呼叫 addCamera / addCarousel 註冊
//   - Carousel 的每幀 debug 視覺（弧線軌跡），由 Producer 每幀呼叫 update()
// 非 #debug 時 add* / update 皆 no-op，呼叫端可無條件呼叫。
export default class Debug {
    constructor() {
        this.active = window.location.hash === '#debug'
        this._carousel = null // addCarousel 後啟用每幀 arc line
        this._camera = null
        this._domElement = null
        this.transformControls = null
        this.showArcLine = true

        if (this.active) {
            this.gui = new GUI({ title: 'Studio Debugger' })
            this.stats = new Stats()

            this._buildPanel()
            this._buildGlobalFolder()
            this._setupKeybindings()
        }
    }

    // 每幀由 Producer.update 呼叫（只有 Carousel 有每幀 debug 視覺）
    update() {
        if (!this.active) {return}

        if (this._carousel && this.arcLine && this.arcLine.visible) {
            this._updateArcLine()
        }
    }

    // ── 全域與鍵盤快捷鍵 ───────────────────────────────

    _setupKeybindings() {
        // 仿 Blender / Maya 快捷鍵：W (Translate), E (Rotate), R (Scale), Q (關閉/顯示手把)
        window.addEventListener('keydown', (e) => {
            if (!this.active || !this.transformControls) {return}
            // 若正在輸入欄位 (GUI 輸入中)，忽略快捷鍵
            if (['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) {return}

            switch (e.key.toLowerCase()) {
                case 'w':
                    this.transformControls.setMode('translate')
                    this.transformControls.enabled = true
                    this.transformControls.visible = true
                    break
                case 'e':
                    this.transformControls.setMode('rotate')
                    this.transformControls.enabled = true
                    this.transformControls.visible = true
                    break
                case 'r':
                    this.transformControls.setMode('scale')
                    this.transformControls.enabled = true
                    this.transformControls.visible = true
                    break
                case 'q':
                    // 切換手把隱藏/顯示
                    this.transformControls.visible = !this.transformControls.visible
                    this.transformControls.enabled = this.transformControls.visible
                    break
            }
        })
    }

    _buildGlobalFolder() {
        const folder = this.gui.addFolder('Global Controls')

        // 全域 Wireframe 模式
        const wireframeController = { wireframe: false }
        folder
            .add(wireframeController, 'wireframe')
            .name('wireframe mode')
            .onChange((enabled) => {
                if (!this._carousel?.group) {return}
                this._carousel.group.traverse((child) => {
                    if (child.isMesh && child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach((m) => {return (m.wireframe = enabled)})
                        } else {
                            child.material.wireframe = enabled
                        }
                    }
                })
            })

        folder.close()
    }

    // ── 元件註冊：Camera ─────────────────────────────
    // 不建 GUI folder：TransformControls 直接改 group 的世界座標，跟相機怎麼看無關，
    // 這裡只需要拿到 camera instance / domElement 供 gizmo 使用。

    addCamera(camera, domElement = document.body) {
        if (!this.active) {return}

        this._camera = camera.instance || camera
        this._cameraControls = camera.controls
        this._domElement = domElement

        // 如果 Carousel 已經先註冊，補綁定 TransformControls
        if (this._carousel && !this.transformControls) {
            this._setupTransformControls()
        }
    }

    // ── 元件註冊：Carousel ───────────────────────────

    addCarousel(carousel) {
        if (!this.active) {return}

        this._carousel = carousel
        this._buildArcLine()
        this._buildCarouselGui()

        // 綁定 3D Transform Gizmo 手把
        if (this._camera && !this.transformControls) {
            this._setupTransformControls()
        }
    }

    // ── 3D Gizmo (Blender 式滑鼠控制) ─────────────────

    _setupTransformControls() {
        if (!this._camera || !this._carousel) {return}

        // 取得 Scene (從 carousel.group 一直往上找 root scene)
        let scene = this._carousel.group
        while (scene.parent) {
            scene = scene.parent
        }

        const dom = this._domElement || document.body
        this.transformControls = new TransformControls(this._camera, dom)
        this.transformControls.size = 0.75 // 調整手把大小
        this.transformControls.attach(this._carousel.group)
        scene.add(this.transformControls.getHelper())

        // 拖拽 Gizmo 時暫停 Camera Controls，避免畫面亂跑
        this.transformControls.addEventListener('dragging-changed', (event) => {
            if (this._cameraControls) {
                this._cameraControls.enabled = !event.value
            }
        })
    }

    // 用一條線畫出當前弧形（跟著 R / SPACING / VISIBLE 即時重畫）
    _buildArcLine() {
        this._arcSegments = 64
        const geometry = new THREE.BufferGeometry()
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array((this._arcSegments + 1) * 3), 3))
        const material = new THREE.LineBasicMaterial({ color: 0x00ffff })
        this.arcLine = new THREE.Line(geometry, material)
        this._carousel.group.add(this.arcLine)
        this._updateArcLine()
    }

    _updateArcLine() {
        const c = this._carousel
        // 畫出可視視窗的弧長範圍 [-halfArc, +halfArc]
        const halfArc = (c.VISIBLE / 2) * c.SPACING
        const pos = this.arcLine.geometry.attributes.position
        for (let i = 0; i <= this._arcSegments; i++) {
            const s = -halfArc + (2 * halfArc * i) / this._arcSegments
            const angle = s / c.R
            pos.setXYZ(i, c.R * Math.sin(angle), 0, c.R * Math.cos(angle) - c.R)
        }
        pos.needsUpdate = true
    }

    _buildCarouselGui() {
        const c = this._carousel

        const folder = this.gui.addFolder('Carousel')

        // 切換弧線顯示/隱藏
        folder
            .add(this, 'showArcLine')
            .name('show arc line')
            .onChange((visible) => {
                if (this.arcLine) {this.arcLine.visible = visible}
            })

        // 3D Gizmo 控制模式選單
        const gizmoControls = { mode: 'translate' }
        folder
            .add(gizmoControls, 'mode', ['translate', 'rotate', 'scale', 'none'])
            .name('🎮 Gizmo Tool (W/E/R)')
            .onChange((mode) => {
                if (!this.transformControls) {return}
                if (mode === 'none') {
                    this.transformControls.enabled = false
                    this.transformControls.visible = false
                } else {
                    this.transformControls.setMode(mode)
                    this.transformControls.enabled = true
                    this.transformControls.visible = true
                }
            })

        folder.add(c, 'curvature', -0.5, 0.5, 0.001).name('curvature (straight→curve)').listen()
        folder.add(c, 'SIZE', 0.1, 3, 0.01).name('square size').listen()
        folder.add(c, 'SPACING', 0.2, 4, 0.05).name('spacing').listen()
        folder
            .add(c, 'VISIBLE', 1, c._maxVisible || 10, 1)
            .name('visible')
            .listen()
        folder.add(c, 'FADE', 0.1, 3, 0.05).name('fade band').listen()
        folder.add(c, 'SENS', 0.0001, 0.01, 0.0001).name('scroll sens').listen()
        folder.add(c, 'DAMP', 0.01, 0.3, 0.01).name('damping').listen()
        folder.add(c, 'DEPTH', 0, 1, 0.01).name('depth').listen()

        const reset = () => {
            c.resetToModeConfig()
        }
        folder.add({ reset }, 'reset').name('↺ reset to mode config')

        this._buildCopyGui(folder)
    }

    // Gizmo 直接改 group.position/rotation（世界座標），已與相機視角無關，可直接抄進 MODE_CONFIG
    _buildCopyGui(folder) {
        const c = this._carousel
        const r = (v) => {
            return Math.round(v * 10000) / 10000
        }

        const copyController = folder
            .add(
                {
                    copy: () => {
                        const p = c.group.position
                        const e = c.group.rotation

                        const code = `{
                pos: [${r(p.x)}, ${r(p.y)}, ${r(p.z)}],
                rot: [${r(e.x)}, ${r(e.y)}, ${r(e.z)}],
                R: ${r(c.R)},
                SPACING: ${r(c.SPACING)},
                VISIBLE: ${Math.round(c.VISIBLE)},
                SENS: ${r(c.SENS)},
                DAMP: ${r(c.DAMP)},
                DEPTH: ${r(c.DEPTH)},
                FADE: ${r(c.FADE)},
                SIZE: ${r(c.SIZE)}
            }`
                        navigator.clipboard?.writeText(code)

                        copyController.name('✅ copied!')
                        setTimeout(() => {return copyController.name('📋 copy as code')}, 1500)
                    }
                },
                'copy'
            )
            .name('📋 copy as code')
    }

    _buildPanel() {
        this._container = document.createElement('div')
        Object.assign(this._container.style, {
            position: 'fixed',
            top: '0',
            right: '0',
            zIndex: '9999',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
        })

        const handle = document.createElement('div')
        handle.textContent = '⠿ debug'
        Object.assign(handle.style, {
            width: '100%',
            boxSizing: 'border-box',
            padding: '4px 8px',
            font: '11px/1.4 monospace',
            color: '#fff',
            background: '#1a1a1a',
            cursor: 'grab',
            userSelect: 'none'
        })

        Object.assign(this.stats.dom.style, { position: 'relative', top: 'auto', left: 'auto' })
        Object.assign(this.gui.domElement.style, { position: 'relative', top: 'auto', right: 'auto' })

        this._container.append(handle, this.stats.dom, this.gui.domElement)
        document.body.appendChild(this._container)

        this._makeDraggable(this._container, handle)
    }

    _makeDraggable(panel, handle) {
        const rect = panel.getBoundingClientRect()
        panel.style.right = 'auto'
        panel.style.left = `${rect.left}px`
        panel.style.top = `${rect.top}px`

        let startX = 0,
            startY = 0,
            originLeft = 0,
            originTop = 0,
            dragging = false

        const onMove = (e) => {
            if (!dragging) {return}
            panel.style.left = `${originLeft + e.clientX - startX}px`
            panel.style.top = `${originTop + e.clientY - startY}px`
        }

        const onUp = () => {
            dragging = false
            handle.style.cursor = 'grab'
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onUp)
        }

        handle.addEventListener('pointerdown', (e) => {
            dragging = true
            startX = e.clientX
            startY = e.clientY
            const r = panel.getBoundingClientRect()
            originLeft = r.left
            originTop = r.top
            handle.style.cursor = 'grabbing'
            window.addEventListener('pointermove', onMove)
            window.addEventListener('pointerup', onUp)
        })
    }

    destroy() {
        if (!this.active) {return}

        if (this.transformControls) {
            this.transformControls.detach()
            this.transformControls.dispose()
        }

        if (this.arcLine) {
            this.arcLine.geometry?.dispose()
            this.arcLine.material?.dispose()
            this.arcLine.removeFromParent()
        }

        this.gui?.destroy()
        if (this._container && this._container.parentNode) {
            this._container.parentNode.removeChild(this._container)
        }

        this._carousel = null
    }
}
